import { useEffect, useState } from "react";
import { UserSidebar } from "@/components/Sidebar";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Trash2, Ban, Lock } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";

const mediaSrc = (u) => (u?.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);

// Group card_number "4111222233334444" -> "4111 2222 3333 4444"
const groupNumber = (n) => (n || "").replace(/(.{4})/g, "$1 ").trim();
const maskNumber = (n) => (n ? "•••• •••• •••• " + n.slice(-4) : "");

function CardVisual({ card, cardBg, showBack, onFlip }) {
  const isBlocked = card.status === "BLOCKED";
  const isCancelled = card.status === "CANCELLED";

  return (
    <div
      className="relative w-full max-w-[380px] mx-auto"
      style={{ perspective: "1200px", aspectRatio: "1.586 / 1" }}
      onClick={() => !isCancelled && onFlip()}
      data-testid={`card-${card.id}`}
      role="button"
    >
      <div
        className="absolute inset-0 transition-transform duration-700"
        style={{ transformStyle: "preserve-3d", transform: showBack ? "rotateY(180deg)" : "rotateY(0)" }}
      >
        {/* FRONT */}
        <div
          className="absolute inset-0 rounded-3xl overflow-hidden shadow-2xl"
          style={{ backfaceVisibility: "hidden", background: "linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 55%, #2a2400 100%)", border: "1px solid rgba(255,214,10,0.35)" }}
        >
          {/* Lion background */}
          {cardBg && (
            <img
              src={cardBg}
              alt=""
              className="absolute right-[-40px] top-1/2 -translate-y-1/2 h-[115%] w-auto opacity-35 select-none pointer-events-none"
              style={{ filter: "drop-shadow(0 4px 14px rgba(0,0,0,0.6))" }}
              draggable="false"
            />
          )}
          {/* Grain / gloss */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/45 to-transparent pointer-events-none"></div>
          <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-black/30 pointer-events-none"></div>

          <div className="relative h-full p-5 sm:p-6 flex flex-col justify-between text-white">
            <div className="flex items-start justify-between">
              <div className="scale-[0.82] sm:scale-100 origin-left" style={{ filter: "drop-shadow(0 2px 8px rgba(0,0,0,0.85))" }}>
                <Logo size="lg" light />
              </div>
              {isBlocked && (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold uppercase bg-red-500/20 text-red-400 border border-red-500/40 backdrop-blur">
                  <Lock size={10} /> Blokirana
                </span>
              )}
              {isCancelled && (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold uppercase bg-white/10 text-white/60 border border-white/20 backdrop-blur">
                  <Ban size={10} /> Stornirana
                </span>
              )}
            </div>

            <div className="flex items-end justify-between">
              <div className="text-white/60 text-[10px] uppercase tracking-widest">Tap za detalje</div>
              <div className="flex flex-col items-end gap-2">
                <div
                  className="w-10 h-7 sm:w-11 sm:h-8 rounded-[5px] relative overflow-hidden shrink-0"
                  style={{
                    background: "linear-gradient(135deg, #f7e08a 0%, #d4af37 40%, #a5822a 70%, #f2dd94 100%)",
                    boxShadow: "0 2px 6px rgba(0,0,0,0.5), inset 0 0 0 1px rgba(255,255,255,0.35)",
                  }}
                  data-testid="card-chip"
                >
                  <span className="absolute left-0 right-0 top-[30%] h-[1px] bg-black/30"></span>
                  <span className="absolute left-0 right-0 top-[62%] h-[1px] bg-black/30"></span>
                  <span className="absolute top-0 bottom-0 left-[32%] w-[1px] bg-black/25"></span>
                  <span className="absolute top-[30%] bottom-[38%] left-[32%] right-[22%] rounded-[2px] border border-black/25"></span>
                </div>
                <img
                  src="https://customer-assets-v7afamib.emergentagent.net/job_stream-payments-2/artifacts/pr7phq82_Visa-Logo-PNG-Image.png"
                  alt="VISA"
                  className="h-5 sm:h-6 w-auto select-none pointer-events-none"
                  style={{ filter: "drop-shadow(0 2px 6px rgba(0,0,0,0.5))" }}
                  draggable="false"
                />
              </div>
            </div>
          </div>
        </div>

        {/* BACK */}
        <div
          className="absolute inset-0 rounded-3xl overflow-hidden shadow-2xl"
          style={{ backfaceVisibility: "hidden", transform: "rotateY(180deg)", background: "linear-gradient(135deg, #131313 0%, #1f1f1f 60%, #FFD60A22 100%)", border: "1px solid rgba(255,214,10,0.35)" }}
        >
          <div className="h-full p-5 sm:p-6 flex flex-col justify-between text-white">
            <div className="w-full h-9 bg-black/70 rounded"></div>
            <div>
              <div className="text-white/50 text-[10px] uppercase tracking-widest mb-1">Broj kartice</div>
              <div className="font-mono font-bold text-lg sm:text-xl tracking-wider text-white" data-testid={`card-num-${card.id}`}>
                {groupNumber(card.card_number)}
              </div>
            </div>
            <div className="flex items-end justify-between gap-4">
              <div>
                <div className="text-white/50 text-[10px] uppercase tracking-widest">Vrijedi do</div>
                <div className="font-mono font-bold text-white" data-testid={`card-exp-${card.id}`}>{card.expiry}</div>
              </div>
              <div>
                <div className="text-white/50 text-[10px] uppercase tracking-widest">CVV</div>
                <div className="font-mono font-bold text-white" data-testid={`card-cvv-${card.id}`}>{card.cvv}</div>
              </div>
              <div className="text-right">
                <div className="text-white/50 text-[10px] uppercase tracking-widest">Vlasnik</div>
                <div className="font-heading font-bold text-white text-sm truncate max-w-[120px]">{card.holder_name}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function VirtualCards() {
  const { user, branding } = useAuth();
  const [cards, setCards] = useState([]);
  const [flipped, setFlipped] = useState({});
  const [confirmDel, setConfirmDel] = useState(null);
  const [busy, setBusy] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [statusLoaded, setStatusLoaded] = useState(false);
  const cardBg = branding?.card_bg_url ? mediaSrc(branding.card_bg_url) : null;

  const load = async () => {
    try { const { data } = await api.get("/cards"); setCards(data); }
    catch (e) { toast.error(formatError(e)); }
  };
  const loadStatus = async () => {
    try { const { data } = await api.get("/cards/status"); setEnabled(!!data.enabled); }
    catch (e) { /* keep default */ }
    finally { setStatusLoaded(true); }
  };
  useEffect(() => { load(); loadStatus(); }, []);

  const create = async () => {
    setBusy(true);
    try {
      await api.post("/cards", {});
      toast.success("Nova kartica kreirana");
      load();
    } catch (e) { toast.error(formatError(e)); }
    setBusy(false);
  };

  const del = async () => {
    try { await api.delete(`/cards/${confirmDel.id}`); toast.success("Kartica obrisana"); setConfirmDel(null); load(); }
    catch (e) { toast.error(formatError(e)); }
  };

  const activeCount = cards.filter((c) => c.status !== "CANCELLED").length;
  const canAdd = activeCount < 4;

  return (
    <div className="stream-dark flex flex-col lg:flex-row min-h-screen">
      <UserSidebar />
      <main className="flex-1 p-4 sm:p-6 lg:p-10 stream-scroll">
        <div className="max-w-5xl">
          <div className="flex items-start justify-between gap-4 flex-wrap mb-6 sm:mb-8">
            <div>
              <h1 className="font-heading font-black text-2xl sm:text-3xl lg:text-4xl text-white">Virtualne kartice</h1>
              <p className="text-white/60 text-sm mt-1">Do 4 kartice. Dodirnite karticu za prikaz podataka.</p>
            </div>
            <Button className="btn-yellow rounded-full h-11 px-5" onClick={create} disabled={!canAdd || busy || !enabled} data-testid="new-card">
              <Plus size={16} className="mr-2" /> Nova kartica ({activeCount}/4)
            </Button>
          </div>

          {statusLoaded && !enabled ? (
            <div className="card-dark p-10 text-center" data-testid="cards-locked">
              <div className="w-16 h-16 rounded-full bg-[#FFD60A]/10 border border-[#FFD60A]/30 flex items-center justify-center mx-auto mb-5">
                <Lock size={28} className="text-[#FFD60A]" />
              </div>
              <h2 className="font-heading font-bold text-xl text-white mb-2">Virtualne kartice još nisu aktivne</h2>
              <p className="text-white/60 text-sm max-w-md mx-auto">
                Virtualne kartice bit će dostupne čim Stripe odobri račun. Nakon aktivacije moći ćete kreirati do 4 kartice.
              </p>
            </div>
          ) : cards.length === 0 ? (
            <div className="card-dark p-10 text-center text-white/60">
              Nemate niti jednu virtualnu karticu. Dodajte prvu klikom gore.
            </div>
          ) : (
            <div className="grid gap-8 sm:grid-cols-2">
              {cards.map((c) => (
                <div key={c.id} className="space-y-3">
                  <CardVisual
                    card={c}
                    cardBg={cardBg}
                    showBack={!!flipped[c.id]}
                    onFlip={() => setFlipped({ ...flipped, [c.id]: !flipped[c.id] })}
                  />
                  <div className="flex items-center justify-between">
                    <div className="text-white/60 text-xs font-mono">{maskNumber(c.card_number)}</div>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-400 hover:bg-red-500/10 h-8"
                      onClick={() => setConfirmDel(c)}
                      data-testid={`del-card-${c.id}`}
                    >
                      <Trash2 size={14} className="mr-1" /> Obriši
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <AlertDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
          <AlertDialogContent className="bg-[#171717] border-white/10 text-white">
            <AlertDialogHeader>
              <AlertDialogTitle>Obrisati karticu?</AlertDialogTitle>
              <AlertDialogDescription className="text-white/60">Ova akcija je nepovratna.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="bg-white/5 border-white/10 text-white">Otkaži</AlertDialogCancel>
              <AlertDialogAction onClick={del} className="bg-red-500 hover:bg-red-600" data-testid="confirm-del-card">Obriši</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </main>
    </div>
  );
}
